/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module GslbModule
 */

import { take } from 'rxjs/operators';

import * as SystemInfoSelectors from 'ng/root-store/system-info/system-info.selectors';

import { withFullModalMixin } from 'ajs/js/utilities/mixins';
import {
    MessageItem,
    ObjectTypeItem,
    RepeatedMessageItem,
} from 'ajs/modules/data-model/factories';

import {
    AviPermissionResource,
    IGslbGeoDbProfile,
} from 'generated-types';

import { L10nService } from '@vmw/ngx-vip';
import { GslbGeoDbProfile } from 'object-types';
import * as l10n from 'ajs/modules/gslb/gslb.l10n';
import { TWindowElement } from 'ajs/modules/data-model/data-model.types';
import { Component, Type } from '@angular/core';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * Interface for IGslbGeoDbProfile config as data.
 */
interface IGslbGeoDbProfileData {
    config: IGslbGeoDbProfile;
}

/**
 * @description GeoProfile Item Class.
 *
 * @author Kondiparthi Shanmukha Sarath, alextsg
 */
export class GslbGeoDbProfileItem extends withFullModalMixin(ObjectTypeItem) {
    public static ajsDependencies = [
        'l10nService',
    ];

    public data: IGslbGeoDbProfileData;

    private readonly l10nService: L10nService;

    constructor(args = {}) {
        const extendedArgs = {
            objectName: 'gslbgeodbprofile',
            windowElement: 'lazy-load',
            objectType: GslbGeoDbProfile,
            permissionName: AviPermissionResource.PERMISSION_GSLBGEODBPROFILE,
            ...args,
        };

        super(extendedArgs);

        this.l10nService = this.getAjsDependency_('l10nService');
        this.l10nService.registerSourceBundles(dictionary);
    }

    /**
     * @override
     */
    public isEditable(): boolean {
        let localSiteIsGslbLeader: boolean;

        this.store.select(SystemInfoSelectors.selectLocalSiteIsGslbLeader)
            .pipe(take(1))
            .subscribe(isLeader => localSiteIsGslbLeader = isLeader);

        return localSiteIsGslbLeader && super.isEditable();
    }

    /**
     * Getter for geoDbEntries.
     */
    public get geoDbEntries(): RepeatedMessageItem<MessageItem> {
        const { entries: geoDbEntries } = this.config;

        return geoDbEntries as RepeatedMessageItem<MessageItem>;
    }

    /**
     * Getter for geoDb Entries count.
     */
    public get geoDbEntriesCount(): number {
        return this.geoDbEntries.count;
    }

    /**
     * Method used to import lazy loaded modal component.
     */
    /* eslint-disable-next-line class-methods-use-this */
    public async getModalComponent(windowElement: TWindowElement): Promise<Type<Component>> {
        const {
            GslbGeoDbProfileModalComponent,
        } = await import(
            /* webpackChunkName: "gslb-geodb-profile-modal" */
            // eslint-disable-next-line max-len
            'ng/lazy-loaded-components/modals/gslb-geodb-profile-modal/gslb-geodb-profile-modal.component'
        );

        return GslbGeoDbProfileModalComponent as Type<Component>;
    }

    /**
     * @override
     */
    protected getModalBreadcrumbTitle(): string {
        return this.l10nService.getMessage(l10nKeys.geoProfileModalBreadcrumbTitle);
    }
}
